import { z } from "zod";
import {
	DictionaryBrandSchemaEnum,
	DictionaryCategorySchemaEnum,
	DictionaryLaboratorySchemaEnum,
	DictionaryPatientSchemaEnum,
	DictionaryProductSchemaEnum,
	DictionaryScheduleTypeSchemaEnum,
	DictionarySupplierSchemaEnum,
	DictionaryUserSchemaEnum,
	EtlImportSchemaEnum,
	ExtractBrandSchemaEnum,
	ExtractCategorySchemaEnum,
	ExtractLaboratorySchemaEnum,
	ExtractPatientSchemaEnum,
	ExtractProductSchemaEnum,
	ExtractScheduleTypeSchemaEnum,
	ExtractSupplierSchemaEnum,
	ExtractUserSchemaEnum
} from "types/src/entities";

export enum EtlApiTarget {
	// auth
	ETL_AUTH = "etl/auth",
	// common
	LABORATORIES_API = "laboratories",
	ETL_IMPORTS_API = "api/etl_imports",
	// extract
	EXTRACT_USERS_API = "api/extract_users",
	EXTRACT_BRANDS_API = "api/extract_brands",
	EXTRACT_CATEGORIES_API = "api/extract_categories",
	EXTRACT_SUPPLIERS_API = "api/extract_suppliers",
	EXTRACT_PRODUCTS_API = "api/extract_products",
	EXTRACT_SCHEDULE_TYPES_API = "api/extract_schedule_types",
	EXTRACT_PATIENTS_API = "api/extract_patients",
	EXTRACT_LABORATORY_API = "api/extract_laboratories",
	// dictionnary
	DICTIONARY_USERS_API = "api/dictionary_users",
	DICTIONARY_BRANDS_API = "api/dictionary_brands",
	DICTIONARY_LABORATORY_API = "api/dictionary_laboratories",
	DICTIONARY_CATEGORIES_API = "api/dictionary_categories",
	DICTIONARY_SUPPLIERS_API = "api/dictionary_suppliers",
	DICTIONARY_PRODUCTS_API = "api/dictionary_products",
	DICTIONARY_SCHEDULE_TYPES_API = "api/dictionary_schedule_types",
	DICTIONARY_PATIENTS_API = "api/dictionary_patients",
	// validate
	LOAD_IMPORT_ETL = "etl/load_import",
	LOAD_MAPPING_ETL = "etl/load_mapping",
	LOAD_EXTRACT_ETL = "etl/load_extract",
	LOAD_DICTIONARY_ETL = "etl/load_dictionary",
	LOAD_OUTPUT_GENERATE = "etl/load_output_generate",
	LOAD_OUTPUT_EXPORT = "etl/load_output_export",
	AW_INSERT = "etl/load_aw_insert",
	CSV_INSPECT_PROCESSING = "etl/load_csv_process_inspect",
	FILE_INSPECT_PROCESSING = "etl/load_file_process_inspect",
	UPLOAD_CSV_TO_S3_BUCKET = "etl/load_upload_csv_to_s3_bucket",
	UPLOAD_FILE_TO_S3_BUCKET = "etl/load_upload_file_to_s3_bucket",
	// PRE-PROCESSING
	UPLOAD_FILE_LABORATORY = ETL_IMPORTS_API + "/:uuid/upload_file/:labId/:fileName",
	UPLOAD_FILE_GLOBAL = ETL_IMPORTS_API + "/:uuid/upload_file/:fileName",

	// ETL STEP :

	// GET
	FILE_COMPETITOR_UPLOADED_STATUS_API = "etl/file_competitor_uploaded_status",
	CSV_UNZIPPER_API = "etl/csv_unzipper",
	FILE_UNZIPPER_API = "etl/file_unzipper",
	CSV_PREPROCESSOR_API = "etl/csv_pre_processor",
	FILES_PREPROCESSOR_API = "etl/files_pre_processor",
	IMPORTER_CSV_API = "etl/csv_importers",
	IMPORTER_PDF_API = "etl/pdf_importers",
	EXTRACT_IMPORTERS_API = "etl/extract_importers",
	DICTIONARY_GENERATORS_API = "etl/dictionary_generators",
	OUTPUT_CSV_GENERATORS_API = "etl/output_csv_generators",
	OUTPUT_DATA_GENERATORS_API = "etl/output_data_generators",
	CSV_UPLOADER_API = "etl/csv_uploader",
	FILE_UPLOADER_API = "etl/file_uploader",
	ORIGINAL_FILE_UPLOADER_API = "etl/original_file_uploader",
	AW_PARSER_API = "etl/aw_parser",

	// POST
	FILE_COMPETITOR_UPLOADER_API = "etl/file_competitor_uploader",
	UNZIP_CSV_API = "etl/unzip_csv",
	UNZIP_FILE_API = "etl/unzip_file",
	PRE_PROCESS_CSV_API = "etl/pre_process_csv",
	PRE_PROCESS_FILES_API = "etl/pre_process_files",
	MERGE_CSV_API = "etl/merge_csv",
	MERGE_FILES_API = "etl/merge_files",
	LOAD_IMPORT_CSV_API = "etl/load_import_csv",
	LOAD_IMPORT_PDF_API = "etl/load_import_pdf",
	LOAD_EXTRACT_IMPORTERS_API = "etl/load_extract_importers",
	GENERATE_DICTIONARY_API = "etl/generate_dictionary",
	GENERATE_OUTPUT_CSV_API = "etl/generate_output_csv",
	GENERATE_OUTPUT_DATA_API = "etl/generate_output_data",
	UPLOAD_CSV_TO_S3_BUCKET_API = "etl/upload_csv_to_aws",
	UPLOAD_FILE_TO_S3_BUCKET_API = "etl/upload_files_to_aws",
	UPLOAD_ORIGINAL_FILE_TO_S3_BUCKET_API = "etl/upload_original_file_to_aws",
	INSERT_DATA_IN_AW_API = "etl/insert_data_in_aw",
	CORRECT_CSV_LINE_MANUALLY_API = "etl/correct_csv_line_manually",
	DOWNLOAD_CSV_API = "etl/download_csv",
	REUPLOAD_CSV_API = "etl/reupload_csv",
}

const targetToSchemasMapping = {
	[EtlApiTarget.ETL_IMPORTS_API]: [
		EtlImportSchemaEnum.EtlImport,
		EtlImportSchemaEnum.BaseEtlImport,
		EtlImportSchemaEnum.EtlImportWithPostProcessingReport,
		EtlImportSchemaEnum.EtlImportWithInsertReport,
		EtlImportSchemaEnum.EtlImportList
	],
	[EtlApiTarget.DICTIONARY_LABORATORY_API]: [
		DictionaryLaboratorySchemaEnum.DictionaryLaboratory
	],
	[EtlApiTarget.DICTIONARY_BRANDS_API]: [
		DictionaryBrandSchemaEnum.DictionaryBrand
	],
	[EtlApiTarget.EXTRACT_BRANDS_API]: [
		ExtractBrandSchemaEnum.ExtractBrand
	],
	[EtlApiTarget.EXTRACT_LABORATORY_API]: [
		ExtractLaboratorySchemaEnum.ExtractLaboratory
	],
	[EtlApiTarget.DICTIONARY_CATEGORIES_API]: [
		DictionaryCategorySchemaEnum.DictionaryCategory
	],
	[EtlApiTarget.EXTRACT_CATEGORIES_API]: [
		ExtractCategorySchemaEnum.ExtractCategory
	],
	[EtlApiTarget.DICTIONARY_SUPPLIERS_API]: [
		DictionarySupplierSchemaEnum.DictionarySupplier
	],
	[EtlApiTarget.EXTRACT_SUPPLIERS_API]: [
		ExtractSupplierSchemaEnum.ExtractSupplier
	],
	[EtlApiTarget.DICTIONARY_USERS_API]: [
		DictionaryUserSchemaEnum.DictionaryUser
	],
	[EtlApiTarget.EXTRACT_USERS_API]: [
		ExtractUserSchemaEnum.ExtractUser
	],
	[EtlApiTarget.DICTIONARY_SCHEDULE_TYPES_API]: [
		DictionaryScheduleTypeSchemaEnum.DictionaryScheduleType
	],
	[EtlApiTarget.EXTRACT_SCHEDULE_TYPES_API]: [
		ExtractScheduleTypeSchemaEnum.ExtractScheduleType
	],
	[EtlApiTarget.DICTIONARY_PATIENTS_API]: [
		DictionaryPatientSchemaEnum.DictionaryPatient
	],
	[EtlApiTarget.EXTRACT_PATIENTS_API]: [
		ExtractPatientSchemaEnum.ExtractPatient
	],
	[EtlApiTarget.DICTIONARY_PRODUCTS_API]: [
		DictionaryProductSchemaEnum.DictionaryProduct
	],
	[EtlApiTarget.EXTRACT_PRODUCTS_API]: [
		ExtractProductSchemaEnum.ExtractProduct
	]
} as const;

export const getTargetFromSchema = (schema: z.ZodSchema): EtlApiTarget => {
	const target = Object.entries(targetToSchemasMapping)
		.find(([_, schemas]) => schemas
			.some((s) => s === schema.description))?.[0];

	if (!target) {
		throw new Error(`No target found for schema ${schema.description}`);
	}

	return target as EtlApiTarget;
};
